import { useEffect, useMemo, useRef, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch, getApiBaseUrl } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { CheckCheck, Plus, ShieldCheck, TriangleAlert } from "lucide-react";

import { workerCeiling } from "@/components/admin/marketing/live-switch-card";
import { socialLiveQueryOptions } from "@/components/admin/marketing/readiness/live-queries";
import { SocialCalendarTab } from "@/components/admin/social/calendar/social-calendar-tab";
import {
  SocialChannelsTab,
  type SocialCronRunSummary,
} from "@/components/admin/social/channels/social-channels-tab";
import { SocialIdeasTab } from "@/components/admin/social/ideas/social-ideas-tab";
import { SocialQueueTab } from "@/components/admin/social/queue/social-queue-tab";
import { SocialSchedulesTab } from "@/components/admin/social/schedules/social-schedules-tab";
import { SocialSettingsTab } from "@/components/admin/social/settings/social-settings-tab";
import { SocialComposerModal } from "@/components/admin/social/social-composer-modal";
import { SocialPostDrawer } from "@/components/admin/social/social-post-drawer";
import {
  type ChannelStatus,
  type PostStatus,
  type Paged,
  type SocialChannel,
  type SocialPost,
} from "@/components/admin/social/types";
import { Button, buttonVariants } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAdminContext } from "@/lib/admin-context";
import { showToast } from "@/lib/ui/toast";

/**
 * Admin → Marketing → Social.
 *
 * Publish-only social engine console. Six URL-driven tabs (same pattern as
 * settings.tsx: validateSearch + isValidTab guard + navigate replace), so every
 * view is linkable — which matters because the review-digest email links
 * straight into a single post:
 *
 *   /marketing-social?tab=queue&status=needs_review&post=<id>
 *
 * That deep link must land on the Queue tab, pre-filtered, with the post's
 * drawer already open. `search.post` is therefore read on mount and drives the
 * drawer, which is owned here (not inside the Queue tab) so calendar chips and
 * mail links open the same instance.
 *
 * Endpoints (contract: _docs/social-automation-plan.md §5, prefix
 * /api/admin/social):
 *   GET  /settings                    → { settings, live, workerEnv, effectiveLive, … }
 *   GET  /channels                    → Paged<SocialChannel> | SocialChannel[]
 *   GET  /channels/:id/connect        → 302 to the platform authorize URL
 *   GET  /cron-runs                   → { items: SocialCronRunSummary[] } (latest run per jobId)
 *
 * DELIVERY MODE — read this before touching the banner. A post reaches a
 * platform only when BOTH layers are open (_docs/marketing-automation-spec.md §1):
 * the SOCIAL_LIVE ceiling on pontimed-workers — the process that publishes,
 * which reports it as `workerEnv` — and the audited admin switch (`live`).
 * The banner shows `effectiveLive` from GET /settings and names the layer that
 * holds it back; apps/api's own env (`live.envAllowed`, the old `socialLive`)
 * is never presented as the delivery mode. The cron-runs heartbeat stays as a
 * secondary signal: a switch that is on does nothing if the crons are dead.
 * The switch itself lives on Settings → Live switch and on /marketing-readiness.
 *
 * Cross-cutting: every fetch effect carries `dbTarget` in its dependency array
 * so the Local↔Prod admin toggle re-runs it; without that the page silently
 * serves the other database's rows.
 */

type SocialTab = "queue" | "calendar" | "channels" | "ideas" | "schedules" | "settings";

const VALID_TABS: SocialTab[] = ["queue", "calendar", "channels", "ideas", "schedules", "settings"];

const VALID_POST_STATUSES: PostStatus[] = [
  "draft",
  "needs_review",
  "blocked",
  "approved",
  "scheduled",
  "publishing",
  "published",
  "failed",
  "cancelled",
];

/**
 * How old the freshest social cron run may be before the banner calls the
 * background pipeline stopped. social-dispatch stamps a fresh row every 5
 * minutes and social-health every hour, so two hours means the workers
 * process is gone.
 */
const WORKER_REPORT_MAX_AGE_MIN = 120;

/** Channel states that mean "this channel will stop publishing without a human". */
const DEGRADED_STATUSES: ChannelStatus[] = ["expired", "revoked", "degraded", "error"];

interface SocialSearch {
  tab?: SocialTab;
  status?: PostStatus;
  post?: string;
  channelId?: string;
  country?: string;
  /** Meta OAuth callback landing: the channel that was connected (one-shot). */
  connected?: string;
  /** Meta OAuth callback landing: why the connection failed (one-shot). */
  error?: string;
}

/** OAuth error codes are short snake_case words; anything else is dropped. */
function asCode(value: unknown): string | undefined {
  return typeof value === "string" && /^[a-z_]{1,40}$/.test(value) ? value : undefined;
}

function isValidTab(value: string): value is SocialTab {
  return (VALID_TABS as string[]).includes(value);
}

function isValidStatus(value: string): value is PostStatus {
  return (VALID_POST_STATUSES as string[]).includes(value);
}

function asId(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : undefined;
}

export const Route = createFileRoute("/_authenticated/marketing-social")({
  validateSearch: (search: Record<string, unknown>): SocialSearch => {
    const rawTab = typeof search.tab === "string" ? search.tab : undefined;
    const rawStatus = typeof search.status === "string" ? search.status : undefined;
    const rawCountry = asId(search.country);
    return {
      tab: rawTab && isValidTab(rawTab) ? rawTab : undefined,
      status: rawStatus && isValidStatus(rawStatus) ? rawStatus : undefined,
      post: asId(search.post),
      channelId: asId(search.channelId),
      country: rawCountry ? rawCountry.toUpperCase().slice(0, 2) : undefined,
      connected: asId(search.connected),
      error: asCode(search.error),
    };
  },
  component: MarketingSocialPage,
});

function MarketingSocialPage() {
  const { t } = useLingui();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { dbTarget } = useAdminContext();

  const activeTab: SocialTab = search.tab ?? "queue";

  // The switch envelope — shared cache with Settings → Live switch and the
  // Readiness page, keyed on dbTarget (live-queries.ts).
  const liveQuery = useQuery(socialLiveQueryOptions(dbTarget));
  const [cronRuns, setCronRuns] = useState<SocialCronRunSummary[] | null>(null);
  // When the heartbeat was read — ages are measured against this, not
  // against Date.now() during render.
  const [cronRunsFetchedAt, setCronRunsFetchedAt] = useState<number | null>(null);
  const [cronRunsUnavailable, setCronRunsUnavailable] = useState(false);
  const [channels, setChannels] = useState<SocialChannel[]>([]);
  const [channelsLoading, setChannelsLoading] = useState(true);
  const [channelsError, setChannelsError] = useState<string | null>(null);
  const [channelsKey, setChannelsKey] = useState(0);
  // The DB the listed channels were read from (see the channels effect).
  const channelsDbRef = useRef<typeof dbTarget | null>(null);
  const [postsKey, setPostsKey] = useState(0);
  const [showComposer, setShowComposer] = useState(false);
  // The post the composer is editing. Without this the drawer's Edit button
  // opened the wizard in CREATE mode, so "save" wrote a SECOND row instead of
  // updating the one on screen — a success toast over a duplicate.
  const [editingPost, setEditingPost] = useState<SocialPost | null>(null);

  // The drawer lives at page level so ?post=<id> deep links, queue rows and
  // calendar chips all drive the same instance. The URL *is* the state — no
  // local mirror to fall out of sync on back/forward.
  const openPostId = search.post ?? null;

  const patchSearch = (next: Partial<SocialSearch>) => {
    void navigate({
      search: (prev: SocialSearch) => ({ ...prev, ...next }),
      replace: true,
    });
  };

  const switchTab = (tab: SocialTab) => patchSearch({ tab });

  // Meta OAuth landing (`/api/admin/social/meta/callback` redirects here with
  // `connected` or `error`). Toast once, then drop the one-shot params so a
  // reload or a shared link does not announce it again.
  useEffect(() => {
    const connectedId = search.connected;
    const errorCode = search.error;
    if (!connectedId && !errorCode) return;
    if (connectedId) {
      showToast(t`Meta account connected — the channel can publish now`, "success");
    } else if (errorCode) {
      showToast(
        (() => {
          switch (errorCode) {
            case "access_denied":
              return t`Meta connection cancelled — access was not granted`;
            case "invalid_state":
            case "session_mismatch":
            case "missing_params":
              return t`The Meta connect link expired or was started in another session — try again`;
            case "not_configured":
              return t`Meta OAuth is not configured on the API`;
            case "token_exchange":
              return t`Meta authorisation failed — the channel card shows why; connect again`;
            case "not_found":
              return t`That channel no longer exists — refresh the list`;
            default:
              return t`Meta connection failed (${errorCode})`;
          }
        })(),
        "error",
      );
    }
    // No refetch needed: the callback lands with a full page load, so the
    // channels list below is fetched fresh on mount.
    void navigate({
      search: (prev: SocialSearch) => ({
        ...prev,
        tab: "channels",
        connected: undefined,
        error: undefined,
      }),
      replace: true,
    });
  }, [search.connected, search.error, navigate, t]);

  // The workers' heartbeat: every social cron writes a SocialCronRun row — the
  // only place a dead cron becomes visible (no DLQ anywhere). The banner uses
  // it as a secondary signal; the delivery mode comes from the switch envelope.
  useEffect(() => {
    let cancelled = false;
    setCronRuns(null);
    setCronRunsUnavailable(false);
    apiFetch
      .get<{ items?: SocialCronRunSummary[] } | SocialCronRunSummary[]>(
        "/api/admin/social/cron-runs",
      )
      .then((data) => {
        if (cancelled) return;
        setCronRuns(Array.isArray(data) ? data : (data?.items ?? []));
        setCronRunsFetchedAt(Date.now());
      })
      .catch(() => {
        if (cancelled) return;
        // 404/501 = the read endpoint is not wired in apps/api yet. Anything
        // else is a real failure; either way the banner says the heartbeat
        // is unavailable rather than implying the crons are fine.
        setCronRuns(null);
        setCronRunsUnavailable(true);
      });
    return () => {
      cancelled = true;
    };
  }, [dbTarget, channelsKey]);

  useEffect(() => {
    let cancelled = false;
    // A refresh after an action on a card (test, connect, mode) re-reads the
    // same DB in the background: the skeleton would unmount every card, and
    // with it what the card was showing, such as a failed test's reason. Only a
    // first load or a Local↔Prod switch shows the skeleton.
    const background = channelsDbRef.current === dbTarget;
    if (!background) setChannelsLoading(true);
    setChannelsError(null);
    apiFetch
      .get<Paged<SocialChannel> | SocialChannel[]>("/api/admin/social/channels")
      .then((data) => {
        if (cancelled) return;
        channelsDbRef.current = dbTarget;
        setChannels(Array.isArray(data) ? data : (data?.items ?? []));
      })
      .catch((e) => {
        if (cancelled) return;
        // A failed background refresh keeps the cards on screen, under the error.
        if (!background) setChannels([]);
        setChannelsError(e instanceof ApiError ? `HTTP ${e.status}` : (e as Error).message);
      })
      .finally(() => {
        if (!cancelled) setChannelsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dbTarget, channelsKey]);

  const refreshChannels = () => setChannelsKey((k) => k + 1);
  const refreshPosts = () => setPostsKey((k) => k + 1);

  const liveEnvelope = liveQuery.data ?? null;
  const ceiling = liveEnvelope ? workerCeiling(liveEnvelope.workerEnv, "social") : null;
  const switchOn = liveEnvelope?.live.enabled === true;

  /**
   * Secondary signal: the freshest social cron run, aged against the moment
   * the heartbeat was read. social-dispatch runs every 5 minutes, so past
   * WORKER_REPORT_MAX_AGE_MIN the background pipeline is treated as stopped —
   * and a stopped pipeline publishes nothing, whatever the switch says.
   */
  const heartbeat = useMemo(() => {
    if (!cronRuns || cronRuns.length === 0 || cronRunsFetchedAt === null) return null;
    // The ledger also carries the marketing-* crons; this banner is about social.
    const social = cronRuns.filter((r) => r.jobId.startsWith("social"));
    const latest = [...(social.length > 0 ? social : cronRuns)].sort(
      (a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime(),
    )[0];
    if (!latest) return null;
    const ageMin = Math.round((cronRunsFetchedAt - new Date(latest.startedAt).getTime()) / 60_000);
    if (!Number.isFinite(ageMin)) return null;
    return {
      jobId: latest.jobId,
      ageMin: Math.max(0, ageMin),
      stale: ageMin > WORKER_REPORT_MAX_AGE_MIN,
    };
  }, [cronRuns, cronRunsFetchedAt]);

  const degraded = channels.filter((c) => DEGRADED_STATUSES.includes(c.status));

  const reconnect = (channel: SocialChannel) => {
    if (channel.canAutomate === false) {
      // Manual-only platform — there is no OAuth handshake to redo; the fix
      // lives on the channel card (credentials / mode), so send the admin there.
      patchSearch({ tab: "channels", channelId: channel.id });
      return;
    }
    window.location.assign(`${getApiBaseUrl()}/api/admin/social/channels/${channel.id}/connect`);
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-foreground text-2xl font-bold tracking-tight">
            <Trans>Social</Trans>
          </h1>
          <p className="text-muted-foreground text-sm">
            <Trans>
              Publish-only engine — generate, review, schedule and publish across every channel
            </Trans>
          </p>
        </div>
        <Button
          size="lg"
          className="min-h-[44px]"
          onClick={() => {
            setEditingPost(null);
            setShowComposer(true);
          }}
        >
          <Plus className="size-4" />
          <Trans>New post</Trans>
        </Button>
      </header>

      {/* Delivery mode — effectiveLive from GET /settings (ceiling reported by
          pontimed-workers AND the admin switch); the heartbeat is secondary. */}
      {liveQuery.isPending ? (
        <Skeleton className="h-24 rounded-lg" />
      ) : (
        <div
          className={
            liveEnvelope?.effectiveLive
              ? "border-success-border bg-success-soft flex items-start gap-3 rounded-lg border p-4"
              : liveEnvelope
                ? "border-warning-border bg-warning-soft flex items-start gap-3 rounded-lg border p-4"
                : "border-border bg-muted/40 flex items-start gap-3 rounded-lg border p-4"
          }
        >
          {liveEnvelope?.effectiveLive ? (
            <CheckCheck className="text-success-strong size-5 shrink-0" aria-hidden />
          ) : liveEnvelope ? (
            <TriangleAlert className="text-warning-strong size-5 shrink-0" aria-hidden />
          ) : (
            <ShieldCheck className="text-muted-foreground size-5 shrink-0" aria-hidden />
          )}
          <div className="min-w-0 flex-1 space-y-1">
            <p
              className={
                liveEnvelope?.effectiveLive
                  ? "text-success-strong text-sm font-semibold"
                  : liveEnvelope
                    ? "text-warning-strong text-sm font-semibold"
                    : "text-foreground text-sm font-semibold"
              }
            >
              {liveEnvelope?.effectiveLive ? (
                <Trans>LIVE — approved posts publish to real accounts</Trans>
              ) : liveEnvelope ? (
                <Trans>Dry-run — nothing is published</Trans>
              ) : (
                <Trans>Delivery mode unverified</Trans>
              )}
            </p>
            <p
              className={liveEnvelope ? "text-foreground text-xs" : "text-muted-foreground text-xs"}
            >
              {!liveEnvelope ? (
                <Trans>
                  The API did not report the social switch. Treat the engine as unverified: this
                  console cannot promise either that posts are going out or that they are not.
                </Trans>
              ) : liveEnvelope.effectiveLive ? (
                <Trans>
                  SOCIAL_LIVE is allowed on pontimed-workers and the admin switch is on. Caps,
                  minimum gaps and the compliance gate still apply.
                </Trans>
              ) : switchOn && ceiling === null ? (
                <Trans>
                  The admin switch is on, but pontimed-workers has not reported its environment yet
                  — treat SOCIAL_LIVE as blocked. Posts are rendered, checked and recorded only.
                </Trans>
              ) : switchOn ? (
                <Trans>
                  The admin switch is on, but SOCIAL_LIVE is not allowed on pontimed-workers. Posts
                  are rendered, checked and recorded only.
                </Trans>
              ) : ceiling === true ? (
                <Trans>
                  The admin switch is off. Posts are rendered, compliance-checked and recorded, but
                  nothing reaches a platform.
                </Trans>
              ) : (
                <Trans>
                  The admin switch is off and SOCIAL_LIVE is not allowed on pontimed-workers. Posts
                  are rendered, compliance-checked and recorded, but nothing reaches a platform.
                </Trans>
              )}
            </p>
            <p className="text-muted-foreground text-xs">
              {cronRunsUnavailable ? (
                <Trans>Background-job heartbeat unavailable.</Trans>
              ) : !heartbeat ? (
                <Trans>
                  No social background job has reported yet — see Channels → Background jobs.
                </Trans>
              ) : heartbeat.stale ? (
                <Trans>
                  The social background jobs last ran {heartbeat.ageMin} minutes before this page
                  loaded ({heartbeat.jobId}). They look stopped — nothing publishes while they are
                  down, whatever the switch says.
                </Trans>
              ) : (
                <Trans>
                  Heartbeat: {heartbeat.jobId} ran {heartbeat.ageMin} minutes before this page
                  loaded.
                </Trans>
              )}
            </p>
            <div className="flex flex-wrap gap-2 pt-1">
              <Link
                to="/marketing-readiness"
                className={cn(buttonVariants({ variant: "outline", size: "sm" }), "min-h-11 px-3")}
              >
                <Trans>Go-live readiness</Trans>
              </Link>
              <Button
                variant="outline"
                size="sm"
                className="min-h-11 px-3"
                onClick={() => switchTab("settings")}
              >
                <Trans>Change the switch</Trans>
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Degraded channels — a dead token is silent otherwise; the schedule just
          stops producing and nobody notices for a week. */}
      {degraded.length > 0 && (
        <section
          aria-label={t`Channels needing attention`}
          className="border-destructive-border bg-destructive-soft space-y-2 rounded-lg border p-3"
        >
          <p className="text-destructive-strong flex items-center gap-2 text-sm font-semibold">
            <TriangleAlert className="size-4 shrink-0" />
            <Trans>{degraded.length} channel(s) will not publish until reconnected</Trans>
          </p>
          <ul className="space-y-2">
            {degraded.map((c) => (
              <li
                key={c.id}
                className="border-destructive-border/60 bg-card flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-sm"
              >
                <span className="text-foreground font-medium">
                  {c.handle ?? c.displayName ?? c.platform}
                </span>
                <span className="text-muted-foreground text-xs uppercase">
                  {c.platform}
                  {c.countryCode ? ` · ${c.countryCode}` : ""}
                </span>
                <span className="text-destructive-strong text-xs">
                  {c.status}
                  {c.lastError ? ` — ${c.lastError}` : ""}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="ml-auto min-h-[44px]"
                  onClick={() => reconnect(c)}
                >
                  <Trans>Reconnect</Trans>
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <Tabs value={activeTab} onValueChange={(v) => switchTab(v as SocialTab)}>
        <TabsList className="flex w-full flex-wrap justify-start sm:w-fit">
          <TabsTrigger value="queue" className="min-h-[44px] flex-none px-3">
            <Trans>Queue</Trans>
          </TabsTrigger>
          <TabsTrigger value="calendar" className="min-h-[44px] flex-none px-3">
            <Trans>Calendar</Trans>
          </TabsTrigger>
          <TabsTrigger value="channels" className="min-h-[44px] flex-none px-3">
            <Trans>Channels</Trans>
          </TabsTrigger>
          <TabsTrigger value="ideas" className="min-h-[44px] flex-none px-3">
            <Trans>Ideas</Trans>
          </TabsTrigger>
          <TabsTrigger value="schedules" className="min-h-[44px] flex-none px-3">
            <Trans>Schedules</Trans>
          </TabsTrigger>
          <TabsTrigger value="settings" className="min-h-[44px] flex-none px-3">
            <Trans>Settings</Trans>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="queue" className="space-y-4 pt-4">
          <SocialQueueTab
            channels={channels}
            channelsLoading={channelsLoading}
            status={search.status ?? ""}
            channelId={search.channelId ?? ""}
            country={search.country ?? ""}
            onFilterChange={(next) => patchSearch(next)}
            onOpenPost={(id) => patchSearch({ post: id })}
            refreshKey={postsKey}
            onDataChanged={refreshPosts}
            onCompose={() => {
              setEditingPost(null);
              setShowComposer(true);
            }}
          />
        </TabsContent>

        <TabsContent value="calendar" className="space-y-4 pt-4">
          <SocialCalendarTab onGoToSchedules={() => switchTab("schedules")} />
        </TabsContent>

        <TabsContent value="channels" className="space-y-4 pt-4">
          <SocialChannelsTab
            channels={channels}
            loading={channelsLoading}
            error={channelsError}
            onRefresh={refreshChannels}
            highlightChannelId={search.channelId}
            cronRuns={cronRuns}
            cronRunsUnavailable={cronRunsUnavailable}
          />
        </TabsContent>

        <TabsContent value="ideas" className="space-y-4 pt-4">
          <SocialIdeasTab />
        </TabsContent>

        <TabsContent value="schedules" className="space-y-4 pt-4">
          <SocialSchedulesTab />
        </TabsContent>

        <TabsContent value="settings" className="space-y-4 pt-4">
          <SocialSettingsTab />
        </TabsContent>
      </Tabs>

      <SocialComposerModal
        open={showComposer}
        onOpenChange={(o: boolean) => {
          setShowComposer(o);
          if (!o) setEditingPost(null);
        }}
        editing={editingPost}
        onSaved={() => {
          refreshPosts();
          refreshChannels();
        }}
      />

      <SocialPostDrawer
        postId={openPostId}
        open={openPostId !== null}
        onOpenChange={(o: boolean) => {
          if (!o) patchSearch({ post: undefined });
        }}
        onUpdated={refreshPosts}
        onEdit={(post: SocialPost) => {
          patchSearch({ post: undefined });
          setEditingPost(post);
          setShowComposer(true);
        }}
      />
    </div>
  );
}
